'use client';

import {
  ArrowTrendingUpIcon, Squares2X2Icon, StarIcon, TrophyIcon, UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { FilterType, MatchStats } from '../screening.types';

interface CandidateStatsFilterProps {
  stats: MatchStats;
  totalCount: number;
  activeFilter: FilterType;
  onFilterChange: (filter: FilterType) => void;
}

const FILTER_CONFIG: Record<FilterType, {
  label: string;
  icon: React.ElementType;
  bgColor: string;
  textColor: string;
  borderColor: string;
  activeBg: string;
  ringColor: string;
}> = {
  all: {
    label: 'All',
    icon: Squares2X2Icon,
    bgColor: 'bg-white',
    textColor: 'text-slate-700',
    borderColor: 'border-slate-200',
    activeBg: 'bg-slate-100',
    ringColor: 'ring-slate-300',
  },
  excellent: {
    label: 'Excellent',
    icon: TrophyIcon,
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
    activeBg: 'bg-emerald-100',
    ringColor: 'ring-emerald-300',
  },
  good: {
    label: 'Good',
    icon: StarIcon,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    borderColor: 'border-blue-200',
    activeBg: 'bg-blue-100',
    ringColor: 'ring-blue-300',
  },
  fair: {
    label: 'Fair',
    icon: ArrowTrendingUpIcon,
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    activeBg: 'bg-amber-100',
    ringColor: 'ring-amber-300',
  },
  poor: {
    label: 'Poor',
    icon: UserGroupIcon,
    bgColor: 'bg-slate-50',
    textColor: 'text-slate-500',
    borderColor: 'border-slate-200',
    activeBg: 'bg-slate-200',
    ringColor: 'ring-slate-300',
  },
};

export const CandidateStatsFilter = ({
  stats,
  totalCount,
  activeFilter,
  onFilterChange,
}: CandidateStatsFilterProps) => {
  const { t } = useTranslation();
  const strongMatchesCount = stats.excellent + stats.good;

  const getCount = (filter: FilterType): number => {
    if (filter === 'all') return totalCount;
    return stats[filter];
  };

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-slate-800">{t('screening.candidates.label')}</h2>
        <p className="text-sm text-slate-500">
          {strongMatchesCount > 0 ? (
            <>
              <span className="font-semibold text-emerald-600">
                {(t('screening.matches.found') || '{nr} strong matches found').replace('{nr}', strongMatchesCount.toString())}
              </span>
            </>
          ) : (
            (t('screening.matches.found') || '{nr} strong matches found').replace('{nr}', '0')
          )}
        </p>
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        {(Object.keys(FILTER_CONFIG) as FilterType[]).map((filter) => {
          const config = FILTER_CONFIG[filter];
          const count = getCount(filter);
          const isActive = activeFilter === filter;
          const Icon = config.icon;

          return (
            <button
              key={filter}
              type="button"
              onClick={() => onFilterChange(filter)}
              className={`
                inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all
                ${isActive
                ? `${config.activeBg} ${config.borderColor} ${config.textColor} ring-2 ring-offset-1 ${config.ringColor}`
                : `${config.bgColor} ${config.borderColor} ${config.textColor} hover:${config.activeBg}`
                }
              `}
            >
              <Icon className="size-4" />
              <span>{count}</span>
              <span>{config.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CandidateStatsFilter;
